import { z } from "zod";
import { responseSchema } from "../http.js";
/**
 * ═══════════════════════════════════════════════════════════
 * Notificaciones del sistema — y los RECORDATORIOS del panel
 *
 * (Anahí, 27-ago): si un socio no tiene cuenta bancaria, el admin le
 * manda un recordatorio y le llega EN el sistema (la campana). Cuando
 * el socio registra su cuenta, el botón de recordar desaparece. Y es
 * UN componente para mandar distintos recordatorios a socios y
 * franquiciatarios — no un botón cosido a una sola pantalla.
 *
 * Por eso los recordatorios son un CATÁLOGO: cada tipo trae su texto
 * aquí, compartido, y el servidor sabe además cuándo un tipo ya no
 * procede (banco registrado, KYC aprobado) para rechazarlo.
 * ═══════════════════════════════════════════════════════════
 */
export const REMINDER_KINDS = ["payout_method", "kyc", "onboarding", "custom"];
export const reminderKindSchema = z.enum(REMINDER_KINDS);
/** El texto de cada recordatorio, UNA vez: lo que ve el socio en su
 * campana y lo que el botón del panel anuncia que va a mandar. */
export const REMINDER_TEMPLATES = {
    payout_method: {
        label: "Recordar cuenta bancaria",
        title: "Registra tu cuenta bancaria",
        body: "Para poder pagarte tus comisiones necesitamos tu cuenta. Agrégala en Mi perfil → Método de pago.",
    },
    kyc: {
        label: "Recordar documentos KYC",
        title: "Sube tus documentos de identidad",
        body: "Tu expediente KYC está incompleto. Súbelo en tu panel para que podamos verificar tu cuenta.",
    },
    onboarding: {
        label: "Recordar primeros pasos",
        title: "Te faltan pasos para arrancar",
        body: "Completa tus primeros pasos para dejar tu cuenta lista.",
    },
    custom: {
        label: "Enviar recordatorio",
        title: "Recordatorio del equipo",
        body: "",
    },
};
/**
 * ═══════════════════════════════════════════════════════════════════
 * Los avisos que nacen SOLOS, sin que nadie pulse un botón.
 *
 * ── Por qué no son recordatorios ─────────────────────────────────────
 * Un recordatorio lo manda una persona desde el panel, y por eso
 * `REMINDER_KINDS` es además el catálogo de botones que esa pantalla
 * ofrece. Meter aquí "tu documentación fue aprobada" pondría en el panel
 * un botón "Recordar KYC aprobado" que no significa nada: nadie recuerda
 * un veredicto, se comunica en el momento en que se emite.
 *
 * Se separan las dos listas y el modelo acepta la UNIÓN. El panel sigue
 * pintando solo los recordatorios; estos los escribe el sistema.
 * ═══════════════════════════════════════════════════════════════════
 */
/**
 * Los avisos que el SISTEMA manda solo, sin que nadie pulse "recordar".
 *
 * ── Por qué crecieron el 1-sep ───────────────────────────────────────
 * Anahí: «en cuanto se registre alguien que le aparezca en su red, en
 * cuanto se le pague a alguien o se le valide una orden notificar por
 * sistema notificaciones correo, y que se actualice su estado; si compró
 * una franquicia, en cuanto se apruebe el otro usuario ya tenga el
 * acceso a su franquicia o el rol de franquiciatario».
 *
 * Lo que YA pasaba solo: al validar una orden, el sistema convierte al
 * comprador en socio, le da `becameFranchiseeAt` si la orden era la
 * franquicia, reinicia su reloj de recompra y corre el motor de
 * comisiones. El rol de franquiciataria nunca hizo falta darlo a mano.
 *
 * Lo que NO pasaba: DECÍRSELO. Todo eso ocurría en silencio —solo el
 * veredicto del KYC avisaba— y quien acababa de pagar una franquicia se
 * quedaba mirando su panel sin saber que ya la tenía. Un cambio de estado
 * que nadie ve es, para quien lo vive, un cambio que no ocurrió.
 */
export const SYSTEM_KINDS = [
    "kyc_approved",
    "kyc_rejected",
    /** Su pedido pasó a validado: los puntos y las comisiones ya cuentan. */
    "order_validated",
    /** La orden validada ERA la franquicia: su panel de negocio ya abre. */
    "franchise_approved",
    /** Le pagaron sus comisiones. */
    "commission_paid",
    /** Su membresía anual vence pronto (30 y 7 días). */
    "membership_expiring",
    /** Su membresía anual venció: deja de cobrar hasta renovar. */
    "membership_expired",
    /** Alguien entró a su red por su enlace. */
    "network_joined",
];
export const systemKindSchema = z.enum(SYSTEM_KINDS);
/** Todo lo que puede aparecer en la campana. */
export const NOTIFICATION_KINDS = [...REMINDER_KINDS, ...SYSTEM_KINDS];
export const notificationKindSchema = z.enum(NOTIFICATION_KINDS);
/**
 * El veredicto del expediente, tal como lo lee quien lo subió.
 *
 * ── El rechazo lleva el motivo, y no es un adorno ────────────────────
 * "Rechazado" a secas obliga a adivinar qué corregir, y lo que hace la
 * gente cuando adivina es volver a subir lo mismo. Por eso el cuerpo
 * deja sitio para la nota que escribió quien revisó; sin nota, al menos
 * dice a dónde ir.
 */
export const KYC_VERDICT_TEMPLATES = {
    kyc_approved: {
        title: "Tu documentación fue aprobada",
        body: "Verificamos tu identidad: tu expediente quedó aprobado. Ya puedes seguir con el resto de tus pasos.",
    },
    kyc_rejected: {
        title: "Tu documentación necesita correcciones",
        body: "Revisamos tu expediente y hay algo que corregir. Vuelve a subir tus documentos en tu panel.",
    },
};
/**
 * El texto de los avisos automáticos, en UN solo lugar.
 *
 * Son funciones y no plantillas fijas porque todos llevan un dato dentro
 * —el folio, el monto, el nombre de quien entró— y ese dato es justo lo
 * que convierte el aviso en algo accionable: "tu pedido quedó validado"
 * sirve mucho menos que "tu pedido ITB-ORD-000014 quedó validado".
 *
 * Vive en el contrato compartido y no en el servicio para que el mismo
 * texto sirva a la campana y al correo, y para que se pueda leer sin
 * abrir el backend.
 */
export const SYSTEM_NOTICES = {
    order_validated: (folio) => ({
        title: "Tu pedido quedó validado",
        body: `Confirmamos el pago de tu pedido ${folio}. Tus puntos ya cuentan y, si invitaste a alguien, sus comisiones también.`,
    }),
    franchise_approved: () => ({
        title: "Tu franquicia ya está activa",
        body: "Validamos tu compra: ya eres franquiciataria. Entra a Mi negocio para poner tu tienda, tu enlace y tu inventario.",
    }),
    commission_paid: (montoLegible) => ({
        title: "Te pagamos tus comisiones",
        body: `Registramos el pago de ${montoLegible} en comisiones. Puedes ver el detalle y el comprobante en Mis comisiones.`,
    }),
    network_joined: (nombre) => ({
        title: "Alguien se unió a tu red",
        body: `${nombre} entró con tu enlace de invitación. Ya aparece en Mi red.`,
    }),
    /** La membresía anual (14-sep-2026): sin ella no se cobran comisiones. */
    membership_expiring: (dias, fechaLegible) => ({
        title: dias <= 7 ? `Tu membresía vence en ${dias} días` : "Tu membresía anual vence pronto",
        body: `Tu membresía anual vence el ${fechaLegible} (en ${dias} días). Renuévala para seguir cobrando las comisiones de tu red: cualquier membresía o paquete la renueva por un año.`,
    }),
    membership_expired: (fechaLegible) => ({
        title: "Tu membresía anual venció",
        body: `Tu membresía venció el ${fechaLegible}. Mientras no la renueves, tus puntos siguen contando pero no cobras comisiones. Renuévala con una membresía o un paquete.`,
    }),
};
export const notificationSchema = z.object({
    id: z.string(),
    kind: notificationKindSchema,
    title: z.string(),
    body: z.string(),
    readAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const myNotificationsResponseSchema = responseSchema(z.object({
    items: z.array(notificationSchema),
    unread: z.number().int().min(0),
}));
/** El admin manda un recordatorio a UNA cuenta. Con `custom`, el
 * mensaje es obligatorio (no hay plantilla que lo supla). */
export const adminSendReminderRequestSchema = z
    .object({
    userId: z.string().trim().min(1),
    kind: reminderKindSchema,
    message: z.string().trim().max(500).optional(),
})
    .refine((value) => value.kind !== "custom" || Boolean(value.message), {
    message: "Un recordatorio personalizado necesita su mensaje",
    path: ["message"],
});
export const sendReminderResponseSchema = responseSchema(z.object({ notification: notificationSchema }));
export const markAllReadResponseSchema = responseSchema(z.object({ marked: z.number().int().min(0) }));
/** Archivar y eliminar confirman lo que hicieron, no se callan. */
export const notificationArchivedResponseSchema = responseSchema(z.object({ archived: z.literal(true) }));
export const notificationDeletedResponseSchema = responseSchema(z.object({ deleted: z.literal(true) }));
export const NOTIFICATION_ROUTES = {
    mine: "/api/notifications/me",
    markAllRead: "/api/notifications/me/read-all",
    /**
     * ── Archivar y eliminar (Anahí, 29-ago) ────────────────────────────
     * La campana pasa de ser una lista que solo se lee a una que se
     * ATIENDE. Son dos cosas distintas y por eso son dos rutas:
     *
     * · archivar — sale de la campana pero SIGUE existiendo. Es "ya la
     *   leí, quítamela de enfrente", que es lo que se quiere el 90% de
     *   las veces y no debería costar un borrado.
     * · eliminar — se va de verdad. Para lo que nunca debió llegar.
     *
     * Una sola ruta que hiciera "quitar" obligaría a decidir por la
     * persona si su aviso se conserva, y esa decisión no es nuestra.
     */
    archive: (id) => `/api/notifications/me/${id}/archivar`,
    remove: (id) => `/api/notifications/me/${id}`,
    adminSend: "/api/admin/notifications",
};
// ═══════════════════════════════════════════════════════════
// Los TRES PRIMEROS PASOS del socio
//
// Los calcula el SERVIDOR y no la pantalla (Anahí, 28-ago). Dos razones:
// el estado sale de cuatro sitios distintos —expediente, direcciones,
// compras y red— y pedirlos por separado desde el navegador son cuatro
// viajes para pintar tres píldoras; y el recordatorio diario tiene que
// nacer aunque nadie abra la campana.
// ═══════════════════════════════════════════════════════════
export const ONBOARDING_STEPS = ["kyc", "addresses", "activity"];
export const onboardingStepSchema = z.enum(ONBOARDING_STEPS);
/** El rótulo corto de cada píldora. */
export const ONBOARDING_STEP_LABELS = {
    kyc: "KYC",
    addresses: "Direcciones",
    activity: "Compra o invita a alguien",
};
export const onboardingSchema = z.object({
    steps: z.array(z.object({ step: onboardingStepSchema, done: z.boolean() })),
    /** Cuántos van cumplidos, para no recontar en la pantalla. */
    doneCount: z.number().int().min(0),
    completed: z.boolean(),
});
export const onboardingResponseSchema = responseSchema(onboardingSchema);
export const ONBOARDING_ROUTES = {
    mine: "/api/onboarding/me",
};
//# sourceMappingURL=notification.js.map